import React, { useState } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import FacturationHeader from './FacturationHeader';
import ModalGenererAvoir from './ModalGenererAvoir';
import { formatMontant } from 'utils/numberToWordsFr';
import { toastApiErrors } from 'utils/toastApiError';
import { useGetFactureByIdQuery } from 'actions/facturation/queries';
import {
  useEmettreFactureMutation,
  useConvertirDevisMutation,
  useEnvoyerFactureMutation,
  useRelancerFactureMutation,
} from 'actions/facturation/mutations';
import '../ventes/client.css';
import './facturation.css';

/**
 * Apercu imprimable d'une facture/devis — modele type Crésus :
 * emetteur + client, lignes, sous-total / TVA / total TTC,
 * montant en lettres, pied de page legal (NINEA, RCCM, tels).
 */
export default function FacturePreview({ factureId, dossierId, onBack, onPayer }) {
  const [showAvoirModal, setShowAvoirModal] = useState(false);
  const { data: f, isFetching, refetch } = useGetFactureByIdQuery({
    id: factureId,
    dossier_id: dossierId,
  });
  const [emettreFacture, { isLoading: emitting }] = useEmettreFactureMutation();
  const [convertirDevis, { isLoading: converting }] = useConvertirDevisMutation();
  const [envoyerFacture, { isLoading: sending }] = useEnvoyerFactureMutation();
  const [relancerFacture, { isLoading: reminding }] = useRelancerFactureMutation();

  const legal = f?.dossier_legal || {};
  const isDevis = f?.type === 'devis';
  const isAvoir = f?.type === 'avoir';

  // Le titre de l'onglet devient le n° du document : c'est lui que le
  // navigateur imprime en en-tete de page.
  React.useEffect(() => {
    if (f?.numero) {
      const prev = document.title;
      document.title = f.numero;
      return () => { document.title = prev; };
    }
  }, [f?.numero]);
  const hasRemise = f?.items?.some((i) => i.remise_percent > 0);

  const onEmettre = async () => {
    try {
      const res = await emettreFacture({ id: f.id }).unwrap();
      toast.success(
        `Facture ${f.numero} émise` +
        (res?.email_envoye
          ? ' et envoyée au client par email'
          : res?.email_erreur ? ` (email non envoyé : ${res.email_erreur})` : '')
      );
      refetch();
    } catch (e) {
      toastApiErrors(e);
    }
  };

  const onEnvoyer = async () => {
    try {
      const res = await envoyerFacture({ id: f.id }).unwrap();
      toast.success(`${isDevis ? 'Devis' : 'Facture'} envoyé(e) à ${res.email_sent_to}`);
      refetch();
    } catch (e) {
      toastApiErrors(e);
    }
  };

  const onConvertir = async () => {
    try {
      const res = await convertirDevis({ id: f.id }).unwrap();
      toast.success(`Devis converti en ${res.numero}`);
      refetch();
    } catch (e) {
      toastApiErrors(e);
    }
  };

  const onAvoir = () => {
    setShowAvoirModal(true);
  };

  const onRelancer = async () => {
    try {
      await relancerFacture({ id: f.id }).unwrap();
      toast.success(`Relance envoyée au client pour ${f.numero}`);
      refetch();
    } catch (e) {
      toastApiErrors(e);
    }
  };

  const statutColor = {
    'Brouillon': { color: '#374151', bg: '#f3f4f6' },
    'Envoyé': { color: '#1e40af', bg: '#dbeafe' },
    'Émis': { color: '#1e40af', bg: '#dbeafe' },
    'Accepté': { color: '#065f46', bg: '#d1fae5' },
    'En attente': { color: '#92400e', bg: '#fef3c7' },
    'Payé': { color: '#065f46', bg: '#d1fae5' },
    'En retard': { color: '#991b1b', bg: '#fee2e2' },
    'Annulé': { color: '#6b7280', bg: '#e5e7eb' },
  }[f?.statut] || { color: '#374151', bg: '#f3f4f6' };

  const nom = (
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">{f?.numero || 'Document'}</h4>
      {f?.statut && (
        <span
          className="nbrDossier"
          style={{ color: statutColor.color, backgroundColor: statutColor.bg }}
        >
          {f.statut}
        </span>
      )}
    </div>
  );

  return (
    <div style={{ backgroundColor: 'white', minHeight: '500px', borderRadius: '20px', padding: '15px' }}>
      <div
        className="header pb-3 no-print"
        style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <FacturationHeader nom={nom} onBack={onBack} />
        {f && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="fact-btn fact-btn-outline-dark"
              style={{ borderRadius: '31px', padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
              onClick={() => window.print()}
            >
              Imprimer / PDF
            </button>
            {f.type === 'facture' && !f.entree && f.statut !== 'Annulé' && (
              <button
                type="button"
                className="fact-btn fact-btn-solid-teal"
                disabled={emitting}
                style={{ borderRadius: '31px', padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
                onClick={onEmettre}
              >
                {emitting ? 'Émission…' : 'Émettre la facture'}
              </button>
            )}
            {f.type === 'facture' && ['En attente', 'En retard'].includes(f.statut) && (
              <button
                type="button"
                className="fact-btn fact-btn-solid-teal"
                style={{ borderRadius: '31px', padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
                onClick={() => onPayer(f)}
              >
                Encaisser
              </button>
            )}
            {f.type === 'facture' && ['En attente', 'En retard'].includes(f.statut) && (
              <button
                type="button"
                className="fact-btn fact-btn-outline-teal"
                disabled={reminding}
                title={`${f.nb_relances || 0} relance(s) envoyée(s)`}
                style={{ borderRadius: '31px', padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
                onClick={onRelancer}
              >
                {reminding ? 'Envoi…' : 'Relancer'}
              </button>
            )}
            {f.type === 'facture' && ['En attente', 'En retard'].includes(f.statut) && (
              <button
                type="button"
                className="fact-btn fact-btn-outline-danger"
                style={{ borderRadius: '31px', padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
                onClick={onAvoir}
              >
                Émettre un avoir
              </button>
            )}
            {isDevis && !['Accepté', 'Annulé'].includes(f.statut) && (
              <button
                type="button"
                className="fact-btn fact-btn-solid-teal"
                disabled={converting}
                style={{ borderRadius: '31px', padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
                onClick={onConvertir}
              >
                {converting ? 'Conversion…' : 'Convertir en facture'}
              </button>
            )}
            {!['Annulé'].includes(f.statut) && (
              <button
                type="button"
                className="fact-btn fact-btn-outline-teal"
                disabled={sending}
                title={f.client_email ? `Envoyer à ${f.client_email}` : "Renseignez d'abord l'email du client"}
                style={{ borderRadius: '31px', padding: '8px 16px', fontSize: '13px', fontWeight: 600 }}
                onClick={onEnvoyer}
              >
                {sending ? 'Envoi…' : 'Envoyer par email'}
              </button>
            )}
          </div>
        )}
      </div>

      {isFetching || !f ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : (
        <div className="facture-doc mx-auto">
          <div className="d-flex justify-content-between">
            <div>
              {legal.logo_url && (
                <img src={legal.logo_url} alt="logo" className="facture-logo" />
              )}
            </div>
            <div className="text-end">
              <h3 className="fw-bold text-uppercase" style={{ color: '#068f7a' }}>
                {isDevis ? 'Devis' : isAvoir ? 'Avoir' : 'Facture'}
              </h3>
              <table className="ms-auto" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #068f7a' }}>
                    <th className="text-end pe-3" style={{ fontWeight: 600 }}>N° FACTURE</th>
                    <th className="text-end" style={{ fontWeight: 600 }}>DATE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-end pe-3">{f.numero}</td>
                    <td className="text-end">{f.date_emission}</td>
                  </tr>
                </tbody>
              </table>
              {isAvoir && f.facture_origine_numero && (
                <div className="small text-muted">Annule {f.facture_origine_numero}</div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-between mt-4 gap-3">
            <div className="facture-client-bloc">
              <div className="fw-bold small" style={{ color: '#068f7a' }}>
                {isDevis ? 'DEVIS À' : isAvoir ? 'CRÉDITÉ À' : 'FACTURER À'}
              </div>
              <div className="fw-bold">{f.client_nom_complet}</div>
              {f.client_adresse && <div>{f.client_adresse}</div>}
              {f.client_telephone && <div>Téléphone : {f.client_telephone}</div>}
              {f.client_email && <div>{f.client_email}</div>}
              {f.client_ninea && <div>NINEA : {f.client_ninea}</div>}
            </div>
            <div className="text-end" style={{ minWidth: 180 }}>
              <div className="fw-bold small" style={{ color: '#068f7a' }}>CONDITIONS</div>
              <div>{f.payment_terms || 'Paiement dû à la réception'}</div>
              {f.date_echeance && !isDevis && <div>Échéance : {f.date_echeance}</div>}
              {isDevis && <div>Validité : {f.validite_jours || 30} jours</div>}
            </div>
          </div>

          <Table size="sm" className="mt-4">
            <thead style={{ backgroundColor: '#068f7a', color: 'white' }}>
              <tr>
                <th>DESCRIPTION</th>
                <th className="text-end" style={{ width: 70 }}>QTÉ</th>
                <th className="text-end" style={{ width: 130 }}>PRIX UNITAIRE</th>
                {hasRemise && <th className="text-end" style={{ width: 90 }}>REMISE</th>}
                <th className="text-end" style={{ width: 140 }}>MONTANT</th>
              </tr>
            </thead>
            <tbody>
              {f.items.map((it) => (
                <tr key={it.id}>
                  <td>{it.description}</td>
                  <td className="text-end">{it.quantite}</td>
                  <td className="text-end">{formatMontant(it.prix_unitaire)}</td>
                  {hasRemise && <td className="text-end">{it.remise_percent || 0} %</td>}
                  <td className="text-end">{formatMontant(it.montant)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-start mt-2">
            <em className="text-muted" style={{ paddingTop: 10 }}>
              Nous vous remercions de votre confiance.
            </em>
            <table className="facture-total-table">
              <tbody>
                <tr><td>SOUS-TOTAL</td><td className="text-end">{formatMontant(f.total_brut_ht)}</td></tr>
                {f.total_remise > 0 && (
                  <>
                    <tr><td>REMISE</td><td className="text-end">-{formatMontant(f.total_remise)}</td></tr>
                    <tr><td className="fw-bold">NET HT</td><td className="text-end fw-bold">{formatMontant(f.net_ht)}</td></tr>
                  </>
                )}
                <tr><td>TAUX TVA</td><td className="text-end">{f.apply_tax ? '18' : '0'} %</td></tr>
                <tr><td>TVA</td><td className="text-end">{formatMontant(f.tva)}</td></tr>
                {f.brs > 0 && (
                  <tr><td>BRS {f.brs_rate} %</td><td className="text-end">-{formatMontant(f.brs)}</td></tr>
                )}
                <tr className="fw-bold" style={{ borderTop: '2px solid #068f7a', backgroundColor: '#f0faf8' }}>
                  <td>TOTAL</td><td className="text-end">{formatMontant(f.total_ttc)} FCFA</td>
                </tr>
                {f.brs > 0 && (
                  <tr className="fw-bold" style={{ color: '#068f7a' }}>
                    <td>NET À PAYER</td><td className="text-end">{formatMontant(f.net_a_payer)} FCFA</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 fst-italic">
            {isDevis ? 'Arrêté le présent devis au montant de' : isAvoir ? 'Arrêté le présent avoir au montant de' : 'Arrêtée la présente facture au montant de'} :{' '}
            <b>{f.montant_lettres}</b>
          </div>

          {f.notes && <div className="mt-2 text-muted small">{f.notes}</div>}

          <div className="d-flex justify-content-end mt-4">
            <div className="facture-signature">
              <b>Signature &amp; Cachet</b>
              {legal.cachet_url && (
                <img src={legal.cachet_url} alt="cachet" className="facture-cachet" />
              )}
            </div>
          </div>

          <div className="facture-footer mt-4 pt-2 border-top text-center small" style={{ color: '#068f7a' }}>
            <div className="fw-semibold">{legal.raison_sociale}</div>
            {legal.adresse && <div>{legal.adresse}</div>}
            <div>
              {legal.telephone ? `Tel : ${legal.telephone}` : ''}
              {legal.telephone && legal.email ? ' / ' : ''}
              {legal.email || ''}
            </div>
            <div className="d-flex justify-content-center flex-wrap gap-2">
              {(legal.nina || legal.ninea) && <span>NINA : {legal.nina || legal.ninea}</span>}
              {(legal.nina || legal.ninea) && (legal.cofi || legal.rccm) && <span>|</span>}
              {legal.cofi && <span>COFI : {legal.cofi}</span>}
              {legal.cofi && legal.rccm && <span>|</span>}
              {legal.rccm && <span>RCCM : {legal.rccm}</span>}
            </div>
            {legal.rib && (
              <div className="mt-1 fw-bold" style={{ color: '#047857' }}>
                Règlement par virement — RIB {legal.banque_nom ? `(${legal.banque_nom})` : ''} : {legal.rib}
              </div>
            )}
          </div>
        </div>
      )}

      <ModalGenererAvoir
        show={showAvoirModal}
        onHide={() => setShowAvoirModal(false)}
        facture={f}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
